import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect } from "react-router-dom";
import { FaPhone, FaEnvelope, FaUserPlus } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

function isDriverRole(role) {
  return String(role || "")
    .trim()
    .toLowerCase()
    .includes("driver");
}

const DriverDetails = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [imageErrorMap, setImageErrorMap] = useState({});

  const loadDrivers = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getStaff.php`, {
        params: { school_code: schoolCode },
      });

      if (res.data?.api_status === "OK" && Array.isArray(res.data?.staff)) {
        setDrivers(res.data.staff.filter((staff) => isDriverRole(staff.staff_role)));
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error("[DriverDetails] load error", error);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  const activeDrivers = useMemo(
    () => drivers.filter((driver) => String(driver.status || "").toLowerCase() !== "inactive"),
    [drivers]
  );

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div className="content" style={{ background: COLORS.whitesmoke }}>
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center">
          <BackButton />
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Driver Details</span>
            </li>
          </ul>
        </div>
        <Link
          to="/AdminMain/StaffRegistration"
          className="btn btn-light btn-sm d-inline-flex align-items-center gap-1"
        >
          <FaUserPlus aria-hidden />
          Add driver
        </Link>
      </div>

      <div className="bg_page_white_rounded box_shadow" style={{ padding: "20px", margin: "16px" }}>
        {loading && <MyLoader />}

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Transport drivers</h4>
            <p className="text-muted mb-0">
              {activeDrivers.length} driver{activeDrivers.length === 1 ? "" : "s"} registered
            </p>
          </div>
        </div>

        {activeDrivers.length === 0 ? (
          <div className="text-center text-muted py-5">
            No drivers found. Register staff with role <strong>Driver</strong>.
          </div>
        ) : (
          <div className="row">
            {activeDrivers.map((driver) => {
              const imageUrl = driver.staff_image_path;
              const showImage = Boolean(imageUrl) && !imageErrorMap[driver.staff_id];
              const initials = String(driver.staff_name || "D")
                .trim()
                .split(/\s+/)
                .slice(0, 2)
                .map((part) => part[0])
                .join("")
                .toUpperCase();

              return (
                <div key={driver.staff_id} className="col-lg-4 col-md-6 mb-3">
                  <div className="p-3 border rounded bg-white h-100">
                    <div className="d-flex align-items-center gap-3 mb-3">
                      {showImage ? (
                        <img
                          src={imageUrl}
                          alt=""
                          className="rounded-circle"
                          style={{ width: 56, height: 56, objectFit: "cover" }}
                          onError={() =>
                            setImageErrorMap((prev) => ({ ...prev, [driver.staff_id]: true }))
                          }
                        />
                      ) : (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center font-weight-bold"
                          style={{
                            width: 56,
                            height: 56,
                            background: "#ccfbf1",
                            color: "#0f766e",
                          }}
                        >
                          {initials}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-weight-bold text-truncate">{driver.staff_name}</div>
                        <div className="small text-muted">{driver.staff_role || "Driver"}</div>
                        <div className="small text-muted">{driver.department_name || "—"}</div>
                      </div>
                    </div>

                    <div className="small mb-2 d-flex align-items-center gap-2">
                      <FaPhone aria-hidden />
                      <span>{driver.staff_phone || "—"}</span>
                    </div>
                    <div className="small mb-3 d-flex align-items-center gap-2">
                      <FaEnvelope aria-hidden />
                      <span className="text-truncate">{driver.staff_email || "—"}</span>
                    </div>

                    <Link
                      to={`/AdminMain/StaffProfile/${encodeURIComponent(String(driver.staff_id))}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      View profile
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverDetails;
